/* eslint-disable no-unused-vars */
import { IHomeStatisticsInput } from "@features/home/helpers/types";
import { COOKIE_ACCESS_TOKEN_KEY } from "@utils/constants";
import { getCookie } from "@utils/Cookies";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

export interface IAppState {
  user: {
    isLoggedIn: boolean;
    isLayoutLoaded: boolean;
    user_id: number | null;
  };
  showFestivalBanner: boolean | undefined;
  statistics: IHomeStatisticsInput | null;
  hideLayout: boolean;
  hideTopbar: boolean;
  hideFooter: boolean;
  hideAppBanner: boolean;
  hideBottomNavigation: boolean;
  withModalAnimation: boolean;
  isAccessPage: boolean;
}
type Functions = {
  setUserLoggedIn: (isLoggedIn: boolean) => void;
  setLayoutLoaded: (loaded: boolean) => void;
  setShowFestivalBanner: (showFestivalBanner: boolean) => void;
  setStatisticsData: (data: IHomeStatisticsInput) => void;
  setUserId: (user_id: number) => void;
  setHideLayout: (hideLayout: boolean) => void;
  setHideTopbar: (hideTopbar: boolean) => void;
  setHideFooter: (hideTopbar: boolean) => void;
  setHideAppBanner: (hideAppBanner: boolean) => void;
  setHideBottomNavigation: (hideTopbar: boolean) => void;
  setWithModalAnimation: (withModalAnimation: boolean) => void;
  setAccessPage: (isAccessPage: boolean) => void;
};
type AppContextType = IAppState & Functions;

type Action = {
  type:
    | "SET_USER_IS_LOGGED_IN"
    | "SET_SHOW_FESTIVAL"
    | "SET_STATISTICS_DATA"
    | "SET_LAYOUT_LOADED"
    | "SET_USER_ID"
    | "HIDE_LAYOUT"
    | "HIDE_TOPBAR"
    | "HIDE_FOOTER"
    | "SET_MODAL_WITHANIMATION"
    | "SET_ACCESS_PAGE"
    | "HIDE_TOPBAR"
    | "HIDE_APP_BANNER"
    | "HIDE_BOTTOM_NAVIGATION";
  payload: any;
};

const initialState: AppContextType = {
  user: {
    isLoggedIn: !!getCookie().get(COOKIE_ACCESS_TOKEN_KEY),
    isLayoutLoaded: false,
    user_id: null,
  },
  showFestivalBanner: undefined,
  statistics: null,
  hideLayout: false,
  hideTopbar: false,
  hideFooter: false,
  hideAppBanner: false,
  hideBottomNavigation: false,
  setUserLoggedIn: () => {},
  setLayoutLoaded: () => {},
  setShowFestivalBanner: () => {},
  setStatisticsData: () => {},
  setUserId: () => {},
  setHideLayout() {},
  setHideTopbar() {},
  setHideFooter() {},
  setHideAppBanner() {},
  setHideBottomNavigation() {},
  withModalAnimation: false,
  setWithModalAnimation: () => {},
  isAccessPage: false,
  setAccessPage: () => {},
};

const AppContext = createContext(initialState);

function appReducer(state: IAppState, action: Action): IAppState {
  switch (action.type) {
    case "SET_USER_IS_LOGGED_IN": {
      return {
        ...state,
        user: {
          ...state.user,
          isLoggedIn: action.payload,
        },
      };
    }
    case "SET_USER_ID": {
      return {
        ...state,
        user: {
          ...state.user,
          user_id: action.payload,
        },
      };
    }
    case "SET_LAYOUT_LOADED": {
      return {
        ...state,
        user: {
          ...state.user,
          isLayoutLoaded: action.payload,
        },
      };
    }

    case "SET_SHOW_FESTIVAL": {
      return {
        ...state,
        showFestivalBanner: action.payload,
      };
    }
    case "SET_STATISTICS_DATA": {
      return {
        ...state,
        statistics: action.payload,
      };
    }
    case "HIDE_LAYOUT": {
      return { ...state, hideLayout: action.payload };
    }
    case "HIDE_TOPBAR": {
      return { ...state, hideTopbar: action.payload };
    }
    case "HIDE_FOOTER": {
      return { ...state, hideFooter: action.payload };
    }
    case "HIDE_APP_BANNER": {
      return { ...state, hideAppBanner: action.payload };
    }
    case "HIDE_BOTTOM_NAVIGATION": {
      return { ...state, hideBottomNavigation: action.payload };
    }
    case "SET_MODAL_WITHANIMATION": {
      return { ...state, withModalAnimation: action.payload };
    }
    case "SET_ACCESS_PAGE": {
      return { ...state, isAccessPage: action.payload };
    }
  }
}

AppContext.displayName = "AppContext";

const AppProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const setHideLayout = useCallback(
    (hideLayout: boolean) =>
      dispatch({ type: "HIDE_LAYOUT", payload: hideLayout }),
    []
  );
  const setHideTopbar = useCallback(
    (hideLayout: boolean) =>
      dispatch({ type: "HIDE_TOPBAR", payload: Boolean(hideLayout) }),
    []
  );
  const setHideAppBanner = useCallback(
    (hideLayout: boolean) =>
      dispatch({ type: "HIDE_APP_BANNER", payload: Boolean(hideLayout) }),
    []
  );
  const setHideFooter = useCallback(
    (hideLayout: boolean) =>
      dispatch({ type: "HIDE_FOOTER", payload: Boolean(hideLayout) }),
    []
  );
  const setHideBottomNavigation = useCallback(
    (hideLayout: boolean) =>
      dispatch({
        type: "HIDE_BOTTOM_NAVIGATION",
        payload: Boolean(hideLayout),
      }),
    []
  );
  const setUserLoggedIn = useCallback(
    (payload: boolean) => dispatch({ type: "SET_USER_IS_LOGGED_IN", payload }),
    []
  );
  const setUserId = useCallback(
    (payload: number) => dispatch({ type: "SET_USER_ID", payload }),
    []
  );
  const setLayoutLoaded = useCallback(
    (payload: boolean) => dispatch({ type: "SET_LAYOUT_LOADED", payload }),
    []
  );

  const setShowFestivalBanner = useCallback(
    (payload: boolean) => [dispatch({ type: "SET_SHOW_FESTIVAL", payload })],
    []
  );
  const setStatisticsData = useCallback(
    (payload: IHomeStatisticsInput) =>
      dispatch({ type: "SET_STATISTICS_DATA", payload }),
    []
  );
  const setWithModalAnimation = useCallback(
    (payload: boolean) =>
      dispatch({ type: "SET_MODAL_WITHANIMATION", payload }),
    []
  );
  const setAccessPage = useCallback(
    (payload: boolean) => dispatch({ type: "SET_ACCESS_PAGE", payload }),
    []
  );

  const value = useMemo(
    () => ({
      ...state,
      setUserLoggedIn,
      setUserId,
      setShowFestivalBanner,
      setStatisticsData,
      setLayoutLoaded,
      setHideLayout,
      setHideTopbar,
      setHideFooter,
      setWithModalAnimation,
      setAccessPage,
      setHideBottomNavigation,
      setHideAppBanner,
    }),
    [state]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error(`useAppContext must be used within a AppProvider`);
  }
  return context;
}

export default AppProvider;
